/**
 * Where equipment stands (z8nrz7fw25): Facility → Building → Floor → Room.
 *
 * Pure, so the rules behind the room picker, the Storage "Locations" view and
 * every "NYP › Building 1 › 2nd Floor › Room 5" line are testable and agree
 * with each other. The tree comes from `GET /facility/locations`; this module
 * only reads it.
 */
import type { LocationRoom, LocationTree, PlacedUnit } from '@/api/facilityApi'
import { formatLocation } from './storageTree'

export type PlaceLevel = 'facility' | 'building' | 'floor' | 'room'

/** One step of an address. `label` is what the path shows for that level. */
export interface PlaceSegment {
  level: PlaceLevel
  id: string
  label: string
}

export const PLACE_SEPARATOR = ' › '

/** "NYP › Building 1 › 2nd Floor › Room 5". */
export function formatPlace(segments: PlaceSegment[]): string {
  return segments.map((s) => s.label).join(PLACE_SEPARATOR)
}

/**
 * Each room's address, by room id. A facility shows its code (it leads every
 * path, so it should be short), the rest their names; a room on no floor is
 * just itself.
 */
export function indexRooms(tree: LocationTree): Map<string, PlaceSegment[]> {
  const out = new Map<string, PlaceSegment[]>()
  for (const f of tree.facilities) {
    const facility: PlaceSegment = { level: 'facility', id: f.id, label: f.code }
    for (const b of f.buildings) {
      const building: PlaceSegment = { level: 'building', id: b.id, label: b.name }
      for (const fl of b.floors) {
        const floor: PlaceSegment = { level: 'floor', id: fl.id, label: fl.name }
        for (const r of fl.rooms) out.set(r.id, [facility, building, floor, roomSegment(r)])
      }
    }
  }
  for (const r of tree.unmappedRooms) out.set(r.id, [roomSegment(r)])
  return out
}

function roomSegment(r: LocationRoom): PlaceSegment {
  return { level: 'room', id: r.id, label: r.label }
}

/** Where each placed piece of equipment stands, by equipment id. Unplaced equipment is absent. */
export function indexUnits(tree: LocationTree): Map<string, PlaceSegment[]> {
  const rooms = indexRooms(tree)
  const out = new Map<string, PlaceSegment[]>()
  for (const r of allRooms(tree)) {
    const at = rooms.get(r.id) ?? [roomSegment(r)]
    for (const u of r.units) out.set(u.id, at)
  }
  return out
}

/**
 * Each storage unit's address, keyed by its code — the first segment of every
 * storage path ("LN2-02/R3/B04/A1"), so any location the app shows can be
 * prefixed with where that unit stands. Units in no room are absent.
 */
export function placesByUnitCode(
  tree: LocationTree,
  units: { code: string; equipmentId: string | null }[],
): Map<string, string> {
  const at = indexUnits(tree)
  const out = new Map<string, string>()
  for (const u of units) {
    const segments = u.equipmentId ? at.get(u.equipmentId) : undefined
    if (segments) out.set(u.code, formatPlace(segments))
  }
  return out
}

/** The address of the unit a storage path starts in, or null when that unit is in no room. */
export function placeOfPath(path: string | null | undefined, places: Map<string, string>): string | null {
  if (!path) return null
  return places.get(path.split('/')[0]) ?? null
}

/**
 * A storage location as one full address, from the site down to the position:
 * "NYP › Building 1 › 2nd Floor › Room 5 › LN2-02 › R3 › B04 › A1". The unit's
 * own path alone while it is in no room, so nothing reads as somewhere it is not.
 */
export function fullLocation(path: string | null | undefined, places: Map<string, string>): string {
  if (!path) return ''
  const inside = formatLocation(path, PLACE_SEPARATOR)
  const place = placeOfPath(path, places)
  return place ? `${place}${PLACE_SEPARATOR}${inside}` : inside
}

/** Every room in the tree, mapped or not. */
export function allRooms(tree: LocationTree): LocationRoom[] {
  return [
    ...tree.facilities.flatMap((f) => f.buildings.flatMap((b) => b.floors.flatMap((fl) => fl.rooms))),
    ...tree.unmappedRooms,
  ]
}

/** The room picker's groups: one per floor, headed by the floor's address, then rooms on no floor. */
export interface RoomOptionGroup {
  heading: string
  rooms: { id: string; label: string; name: string | null }[]
}

export function roomOptionGroups(tree: LocationTree): RoomOptionGroup[] {
  const groups: RoomOptionGroup[] = []
  for (const f of tree.facilities) {
    for (const b of f.buildings) {
      for (const fl of b.floors) {
        if (fl.rooms.length === 0) continue
        groups.push({
          heading: [f.code, b.name, fl.name].join(PLACE_SEPARATOR),
          rooms: fl.rooms.map((r) => ({ id: r.id, label: r.label, name: r.name })),
        })
      }
    }
  }
  if (tree.unmappedRooms.length > 0) {
    groups.push({
      heading: 'Rooms not on a floor yet',
      rooms: tree.unmappedRooms.map((r) => ({ id: r.id, label: r.label, name: r.name })),
    })
  }
  return groups
}

/**
 * Every floor a room can be put on, as its full address ("NYP › Building 1 ›
 * 2nd Floor"), in layout order — empty floors included, since a new room is
 * often the first thing on one.
 */
export function floorOptions(tree: LocationTree): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = []
  for (const f of tree.facilities) {
    for (const b of f.buildings) {
      for (const fl of b.floors) out.push({ value: fl.id, label: [f.code, b.name, fl.name].join(PLACE_SEPARATOR) })
    }
  }
  return out
}

/** The counts the Locations view leads with. */
export function locationStats(tree: LocationTree) {
  const rooms = allRooms(tree)
  return {
    facilities: tree.facilities.length,
    buildings: tree.facilities.reduce((n, f) => n + f.buildings.length, 0),
    rooms: rooms.length,
    placed: rooms.reduce((n, r) => n + r.units.length, 0),
    unplaced: tree.unplaced.length,
  }
}

const matches = (text: string | null | undefined, q: string) => !!text && text.toLowerCase().includes(q)

/**
 * The tree narrowed to what a search names. A room is kept when it, or a unit
 * in it, matches; a matching facility, building or floor keeps everything
 * beneath it, since "Building 1" means all of Building 1. Emptied levels go.
 * A blank query returns the tree as it is.
 */
export function filterTree(tree: LocationTree, query: string): LocationTree {
  const q = query.trim().toLowerCase()
  if (!q) return tree
  const unitHit = (u: PlacedUnit) => matches(u.label, q) || matches(u.typeName, q)
  const roomHit = (r: LocationRoom) => matches(r.label, q) || matches(r.name, q) || r.units.some(unitHit)
  const keepRoom = (r: LocationRoom): LocationRoom =>
    matches(r.label, q) || matches(r.name, q) ? r : { ...r, units: r.units.filter(unitHit) }

  return {
    facilities: tree.facilities
      .map((f) => {
        if (matches(f.code, q) || matches(f.name, q)) return f
        return {
          ...f,
          buildings: f.buildings
            .map((b) => {
              if (matches(b.code, q) || matches(b.name, q)) return b
              return {
                ...b,
                floors: b.floors
                  .map((fl) => (matches(fl.name, q) ? fl : { ...fl, rooms: fl.rooms.filter(roomHit).map(keepRoom) }))
                  .filter((fl) => fl.rooms.length > 0 || matches(fl.name, q)),
              }
            })
            .filter((b) => b.floors.length > 0),
        }
      })
      .filter((f) => f.buildings.length > 0 || matches(f.code, q) || matches(f.name, q)),
    unmappedRooms: tree.unmappedRooms.filter(roomHit).map(keepRoom),
    unplaced: tree.unplaced.filter(unitHit),
  }
}
